import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import CallEndIcon from '@mui/icons-material/CallEnd';

export default function CallControls({
  micEnabled,
  cameraEnabled,
  onToggleMic,
  onToggleCamera,
  onHangup,
  /** Disables the microphone and camera toggles. Those act on the local media
   *  tracks, so they need the LiveKit local participant to exist. */
  disabled = false,
  /** Hang up is deliberately separate and enabled by default. Ending a call is
   *  a server action, not a media one, so it must stay available even when the
   *  SDK has no local participant: a therapist reported being unable to end a
   *  call while mute and camera were greyed out (ST-20260916-0003, 16 Sep 2026).
   *  Only pass this while the hangup request itself is in flight. */
  hangupDisabled = false,
  /** Optional ReactNode rendered between the camera toggle and the
   *  hangup button — used to surface in-call tools (e.g. send-assessment
   *  menu) right next to where the therapist's attention already is. */
  extraActions = null,
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        justifyContent: 'center',
        alignItems: 'center',
        py: 2
      }}
    >
      <Tooltip title={micEnabled ? 'Mute microphone' : 'Unmute microphone'}>
        <span>
          <IconButton
            onClick={onToggleMic}
            aria-label={micEnabled ? 'Mute microphone' : 'Unmute microphone'}
            disabled={disabled}
            sx={{
              bgcolor: micEnabled ? 'grey.200' : 'error.main',
              color: micEnabled ? 'text.primary' : 'common.white',
              '&:hover': { bgcolor: micEnabled ? 'grey.300' : 'error.dark' },
              width: 56,
              height: 56
            }}
          >
            {micEnabled ? <MicIcon /> : <MicOffIcon />}
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={cameraEnabled ? 'Turn off camera' : 'Turn on camera'}>
        <span>
          <IconButton
            onClick={onToggleCamera}
            aria-label={cameraEnabled ? 'Turn off camera' : 'Turn on camera'}
            disabled={disabled}
            sx={{
              bgcolor: cameraEnabled ? 'grey.200' : 'error.main',
              color: cameraEnabled ? 'text.primary' : 'common.white',
              '&:hover': { bgcolor: cameraEnabled ? 'grey.300' : 'error.dark' },
              width: 56,
              height: 56
            }}
          >
            {cameraEnabled ? <VideocamIcon /> : <VideocamOffIcon />}
          </IconButton>
        </span>
      </Tooltip>

      {extraActions}

      <Tooltip title="End call">
        <span>
          <IconButton
            onClick={onHangup}
            aria-label="End call"
            disabled={hangupDisabled}
            sx={{
              bgcolor: 'error.main',
              color: 'common.white',
              '&:hover': { bgcolor: 'error.dark' },
              width: 64,
              height: 64,
              ml: 2
            }}
          >
            <CallEndIcon fontSize="large" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}
